// origin_brand App Proxy — 마이페이지 도메인 origin 저장/복귀.
// 저장값 = 진입 시점 도메인(host). 복귀 = 저장 host + 메뉴 쿼리(?mtk/?mfg)로 브랜드 home 경로.
// 공개경로: HK=/tools/origin-brand, TW=/apps/origin-brand → 후보 순차 시도(404 폴백)로 양쪽 동일 코드.
//   경로 차이는 영구 아님 — TW 앱이 tools prefix 로 재배포되면 둘 다 /tools (폴백은 무해한 dead path).
// BE: custom.origin_brand (free string). Shopify 가 signature / logged_in_customer_id 를 MSA 로 전달.
// 문서: docs/be-handoff/origin-brand-api-client.md
(() => {
  'use strict';

  const cfg = window.MISTO_ORIGIN_BRAND;
  if (!cfg) return;

  const PROXY_CANDIDATES = cfg.proxyPaths?.length ? [...cfg.proxyPaths] : ['/tools/origin-brand', '/apps/origin-brand'];
  const PATHS = cfg.paths ?? { mtk: '/pages/mtk-home', mfg: '/pages/mfg-home' };
  const DEFAULT_HOST = cfg.defaultHost ?? ''; // 저장값 없거나 못 읽을 때 보낼 기본 브랜드 도메인(마리떼)
  const ALLOWED_HOSTS = cfg.allowedHosts ?? []; // 복귀 허용 host allowlist (open-redirect 방지)
  let resolvedProxy = null; // 한 번 성공한 경로 캐시

  // 메타필드 host 가 우리 도메인 목록에 있을 때만 허용. 미설정 시 게이트 비활성(하위호환).
  const isAllowedHost = (host) => ALLOWED_HOSTS.length === 0 || ALLOWED_HOSTS.includes(host);

  // 후보 경로를 순서대로 시도. 404(스토어에 없는 prefix)면 다음 후보로 폴백.
  // 401(customer_login_required)은 경로가 맞다는 뜻이라 폴백하지 않는다.
  const proxyFetch = async (options) => {
    const candidates = resolvedProxy ? [resolvedProxy] : PROXY_CANDIDATES;
    let lastError;
    for (let i = 0; i < candidates.length; i += 1) {
      try {
        const res = await fetch(candidates[i], options);
        if (res.status === 404 && i < candidates.length - 1) continue; // 다음 후보로 폴백
        if (res.ok) resolvedProxy = candidates[i];
        return res;
      } catch (error) {
        lastError = error; // 네트워크 에러 → 다음 후보 시도
      }
    }
    throw lastError ?? new Error('origin_brand proxy unreachable');
  };

  const getOriginDomain = async () => {
    const res = await proxyFetch({
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error('origin_brand load failed');
    return res.json();
  };

  // 저장값 = 도메인(host). BE 필드는 custom.origin_brand 그대로 사용 (free string).
  const saveOriginDomain = async (host) => {
    const res = await proxyFetch({
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ origin_brand: host }),
    });
    if (!res.ok) throw new Error('origin_brand save failed');
    return res.json();
  };

  // 저장값이 host("www.x.com") 든 full URL("https://www.x.com/...") 든 host 만 추출.
  const sanitizeHost = (value) => {
    const raw = String(value ?? '').trim();
    if (!raw) return '';
    try {
      return new URL(raw.includes('//') ? raw : `https://${raw}`).host;
    } catch {
      return raw;
    }
  };

  // 별칭(alias) 도메인 위에 있을 때만 복귀 발동. aliasHosts 미설정 시 게이트 비활성(하위호환).
  const isOnAliasHost = () => {
    const hosts = cfg.aliasHosts ?? [];
    return hosts.length === 0 || hosts.includes(window.location.host);
  };

  // 메뉴 쿼리에서 브랜드 키 판정. 사용자 규약(?mtk/?mfg) + legacy(?brand=mtk/mfg) 호환.
  const brandKeyFromQuery = () => {
    const params = new URLSearchParams(window.location.search);
    const brand = params.get('brand');
    if (params.has('mtk') || brand === 'mtk') return 'mtk';
    if (params.has('mfg') || brand === 'mfg' || brand === 'marithe') return 'mfg';
    return '';
  };

  const normalizeLocation = (url) => {
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.origin + (parsed.pathname.replace(/\/$/, '') || '/');
    } catch {
      return url;
    }
  };

  const needsRedirect = (targetUrl) => normalizeLocation(window.location.href) !== normalizeLocation(targetUrl);

  const cleanRedirectQueryParams = () => {
    try {
      const { search, pathname, hash } = window.location;
      const params = new URLSearchParams(search);
      let changed = false;
      for (const key of ['mtk', 'mfg', 'from', 'brand']) {
        if (params.has(key)) {
          params.delete(key);
          changed = true;
        }
      }
      if (!changed) return;
      const query = params.toString();
      window.history.replaceState({}, '', pathname + (query ? `?${query}` : '') + hash);
    } catch {
      /* ignore */
    }
  };

  // 복귀: 메뉴 쿼리로 브랜드 home 경로 결정 + 저장 host 로 도메인 복원.
  // 저장값 없거나 못 읽으면 기본 브랜드 도메인(마리떼)으로.
  const redirectToOriginDomain = async () => {
    if (!cfg.customerLoggedIn || !isOnAliasHost()) return; // 로그인 + 별칭 도메인에서만 발동
    const brandKey = brandKeyFromQuery();
    if (!brandKey) return; // 트리거 쿼리 없으면 동작 안 함
    const path = PATHS[brandKey] ?? '/';

    let targetHost;
    try {
      const data = await getOriginDomain();
      const savedHost = data?.ok ? sanitizeHost(data.origin_brand ?? data.brand) : '';
      // allowlist 통과한 host 만 사용. 미등록(또는 빈 값)이면 기본 브랜드 도메인(마리떼)으로.
      const safeHost = isAllowedHost(savedHost) ? savedHost : '';
      targetHost = safeHost || DEFAULT_HOST || window.location.host;
    } catch {
      targetHost = DEFAULT_HOST || window.location.host; // API 실패(401·네트워크 등)
    }

    const targetUrl = `https://${targetHost}${path}`;
    if (needsRedirect(targetUrl)) {
      window.location.replace(targetUrl);
      return;
    }
    cleanRedirectQueryParams();
  };

  // 진입: 계정 링크 클릭 시 현재 도메인(host) 저장 후 이동. 저장 성공/실패와 무관하게 이동.
  const saveOriginAndNavigate = async (url, event) => {
    const isModified =
      event && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0);

    // 🔴 새 탭/새 창(modifier·가운데클릭) — preventDefault 하지 않아 브라우저 기본(새 탭) 유지.
    // 현재 페이지는 그대로 남으므로 저장만 백그라운드로 보낸다.
    if (isModified) {
      if (cfg.customerLoggedIn) saveOriginDomain(window.location.host).catch(() => {});
      return;
    }

    // 일반 좌클릭 — 기본 이동 막고 저장 완료 후 같은 탭 이동.
    if (event) event.preventDefault();
    if (cfg.customerLoggedIn) {
      try {
        await saveOriginDomain(window.location.host);
      } catch {
        /* 저장 실패해도 진입 막지 않음 */
      }
    }
    window.location.href = url;
  };

  const bindAccountLinks = () => {
    document.addEventListener('click', (event) => {
      if (!cfg.customerLoggedIn) return;
      const link = event.target?.closest?.('a[data-misto-account-entry]');
      const url = link?.getAttribute('href');
      if (url) saveOriginAndNavigate(url, event);
    });
  };

  const init = () => {
    redirectToOriginDomain();
    bindAccountLinks();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
